import React, { useCallback, useEffect, useState } from "react";
import {
  getExamMarksAnalytics,
  getExamMarksAnalyticsPortal,
} from "../../api/examsschedule/examMarksAnalyticsApi";
import ChartCard from "./ChartCard";
import DonutChart from "./DonutChart";
import BarChart from "./BarChart";
import GroupedBarChart from "./GroupedBarChart";
import AreaChart from "./AreaChart";
import { useToast } from "../../context/ToastContext";

/* Exam/Marks palette — distinct from fee/payroll/staff */
const C = {
  sky: "#0284C7",
  orange: "#EA580C",
  emerald: "#059669",
  amber: "#D97706",
  rose: "#E11D48",
  violet: "#7C3AED",
  slate: "#64748B",
};

const STATUS_COLORS = [C.slate, C.sky, C.amber, C.emerald, C.violet];
const ASSIGN_COLORS = [C.amber, C.sky, C.emerald, C.violet];
const MARKS_COLORS = [C.emerald, C.rose, C.slate];

const PERIOD_OPTIONS = [
  { value: "month", label: "This Month" },
  { value: "year", label: "This Year" },
  { value: "all", label: "Calendar Year" },
];

const MONTHS = [
  { value: "", label: "All months" },
  ...Array.from({ length: 12 }, (_, i) => ({
    value: String(i + 1),
    label: ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"][i],
  })),
];

/* ---- Decorative KPI tile with icon ---- */
const IconKPI = ({ icon, label, value, sub, tone = "default" }) => {
  const tones = {
    default: "bg-white dark:bg-neutral-900 border-slate-200 dark:border-neutral-800",
    success: "bg-emerald-50 border-emerald-100",
    warning: "bg-amber-50 border-amber-100",
    danger: "bg-rose-50 border-rose-100",
    info: "bg-sky-50 border-sky-100",
  };
  const valueTone = {
    default: "text-slate-900 dark:text-neutral-100",
    success: "text-emerald-700",
    warning: "text-amber-700",
    danger: "text-rose-700",
    info: "text-sky-700",
  };
  return (
    <div
      className={`flex items-start gap-3 rounded-xl border p-3 shadow-sm ${tones[tone] || tones.default}`}
    >
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white/80 dark:bg-neutral-900/80 text-lg shadow-sm">
        {icon}
      </div>
      <div className="min-w-0">
        <p className="text-[11px] font-medium uppercase tracking-wide text-slate-500 dark:text-neutral-400">
          {label}
        </p>
        <p className={`text-xl font-semibold tabular-nums leading-tight ${valueTone[tone] || valueTone.default}`}>
          {value ?? "—"}
        </p>
        {sub && <p className="mt-0.5 text-[10px] text-slate-500 dark:text-neutral-400">{sub}</p>}
      </div>
    </div>
  );
};

const ProgressTrack = ({ label, percent, color = C.sky }) => (
  <div className="space-y-1">
    <div className="flex items-center justify-between text-xs">
      <span className="font-medium text-slate-600 dark:text-neutral-300">{label}</span>
      <span className="tabular-nums text-slate-800 dark:text-neutral-100">{percent ?? 0}%</span>
    </div>
    <div className="h-2 overflow-hidden rounded-full bg-slate-100 dark:bg-neutral-900">
      <div
        className="h-full rounded-full transition-all duration-500"
        style={{
          width: `${Math.min(100, Math.max(0, percent || 0))}%`,
          backgroundColor: color,
        }}
      />
    </div>
  </div>
);

const LoadingState = () => (
  <div className="flex h-40 items-center justify-center">
    <div className="h-6 w-6 animate-spin rounded-full border-2 border-orange-500 border-t-transparent" />
  </div>
);

const ErrorState = ({ message, onRetry }) => (
  <div className="flex flex-col items-center gap-2 rounded-lg border border-rose-200 bg-rose-50 px-4 py-6 text-center">
    <p className="text-sm text-rose-700">{message || "Failed to load analytics"}</p>
    {onRetry && (
      <button
        onClick={onRetry}
        className="rounded-md bg-rose-600 px-3 py-1 text-xs text-white hover:bg-rose-700"
      >
        Retry
      </button>
    )}
  </div>
);

const ExamMarksAnalytics = ({ isPortal = false }) => {
  const now = new Date();
  const [period, setPeriod] = useState("year");
  const toast = useToast();
  const [error, setError] = useState(null);

  const [year, setYear] = useState(String(now.getFullYear()));
  const [month, setMonth] = useState("");
  const [academicYear, setAcademicYear] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { period, year };
      if (month) params.month = month;
      if (academicYear) params.academicYear = academicYear;

      const result = isPortal
        ? await getExamMarksAnalyticsPortal(params)
        : await getExamMarksAnalytics(params);
      setData(result);
    } catch (err) {
      console.error(err);
      setError(err?.response?.data?.message || err.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  }, [period, year, month, academicYear, isPortal]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const kpis = data?.kpis || {};
  const distributions = data?.distributions || {};
  const trends = data?.trends || {};
  const years = data?.filterOptions?.years?.length
    ? data.filterOptions.years
    : [now.getFullYear(), now.getFullYear() - 1];
  const academicYears = data?.filterOptions?.academicYears || [];

  return (
    <div className="space-y-3">
      <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
        <h2 className="text-sm font-semibold text-slate-700 dark:text-neutral-200">Exams & Marks Overview</h2>

        <div className="flex flex-wrap items-center gap-2">
          <select
            value={year}
            onChange={(e) => setYear(e.target.value)}
            className="rounded-md border border-slate-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 px-2 py-1 text-xs text-slate-700 dark:text-neutral-200"
          >
            {years.map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>

          <select
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            className="rounded-md border border-slate-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 px-2 py-1 text-xs text-slate-700 dark:text-neutral-200"
          >
            {MONTHS.map((m) => (
              <option key={m.value || "all"} value={m.value}>{m.label}</option>
            ))}
          </select>

          {academicYears.length > 0 && (
            <select
              value={academicYear}
              onChange={(e) => setAcademicYear(e.target.value)}
              className="rounded-md border border-slate-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 px-2 py-1 text-xs text-slate-700 dark:text-neutral-200"
            >
              <option value="">All academic years</option>
              {academicYears.map((ay) => (
                <option key={ay} value={ay}>{ay}</option>
              ))}
            </select>
          )}

          <div className="flex gap-1">
            {PERIOD_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                onClick={() => setPeriod(opt.value)}
                className={`rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${ period === opt.value ? "bg-orange-600 text-white" : "bg-slate-100 dark:bg-neutral-900 text-slate-600 dark:text-neutral-300 hover:bg-slate-200" }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {loading && <LoadingState />}
      {!loading && error && <ErrorState message={error} onRetry={fetchData} />}

      {!loading && !error && data && (
        <>
          {/* Decorative icon KPIs */}
          <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
            <IconKPI
              icon="📋"
              label="Exams"
              value={kpis.totalExams}
              sub={`${kpis.ongoingExams || 0} ongoing`}
              tone="info"
            />
            <IconKPI
              icon="📅"
              label="Schedules"
              value={kpis.totalSchedules}
              sub={`${kpis.schedulesCompleted || 0} completed`}
            />
            <IconKPI
              icon="✍️"
              label="Mark assignments"
              value={kpis.totalAssignments}
              sub={`${kpis.submittedAssignments || 0} submitted`}
              tone="success"
            />
            <IconKPI
              icon="⚠️"
              label="Overdue"
              value={kpis.overdueAssignments}
              sub="past deadline"
              tone={kpis.overdueAssignments > 0 ? "danger" : "default"}
            />
          </div>

          {/* Progress-style analytics (different from other modules) */}
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <div className="rounded-xl border border-slate-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 p-4 shadow-sm space-y-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-neutral-400">
                Assignment pipeline
              </p>
              <ProgressTrack
                label="Submission rate"
                percent={kpis.submissionRate}
                color={C.emerald}
              />
              <ProgressTrack
                label="Marks entry coverage"
                percent={kpis.entryRate}
                color={C.sky}
              />
              <div className="flex flex-wrap gap-3 pt-1 text-[11px] text-slate-600 dark:text-neutral-300">
                <span>Pending <b>{kpis.pendingAssignments || 0}</b></span>
                <span>In progress <b>{kpis.inProgressAssignments || 0}</b></span>
                <span>Locked <b>{kpis.lockedAssignments || 0}</b></span>
              </div>
            </div>

            <div className="rounded-xl border border-slate-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 p-4 shadow-sm space-y-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-neutral-400">
                Score snapshot
              </p>
              <div className="flex items-end gap-2">
                <span className="text-3xl font-semibold tabular-nums text-orange-600">
                  {kpis.avgScorePct != null ? `${kpis.avgScorePct}%` : "—"}
                </span>
                <span className="pb-1 text-xs text-slate-500 dark:text-neutral-400">avg of entered marks</span>
              </div>
              <div className="grid grid-cols-3 gap-2 text-center text-xs">
                <div className="rounded-lg bg-emerald-50 p-2">
                  <p className="font-semibold text-emerald-700">{kpis.marksEntered || 0}</p>
                  <p className="text-slate-500 dark:text-neutral-400">Entered</p>
                </div>
                <div className="rounded-lg bg-rose-50 p-2">
                  <p className="font-semibold text-rose-700">{kpis.marksAbsent || 0}</p>
                  <p className="text-slate-500 dark:text-neutral-400">Absent</p>
                </div>
                <div className="rounded-lg bg-slate-50 dark:bg-neutral-950 p-2">
                  <p className="font-semibold text-slate-700 dark:text-neutral-200">{kpis.marksPending || 0}</p>
                  <p className="text-slate-500 dark:text-neutral-400">Pending</p>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
            <ChartCard title="Assignment status">
              <DonutChart
                data={distributions.byAssignmentStatus || []}
                colors={ASSIGN_COLORS}
                height={200}
              />
            </ChartCard>
            <ChartCard title="Marks entry mix">
              <DonutChart
                data={distributions.marksBreakdown || []}
                colors={MARKS_COLORS}
                height={200}
              />
            </ChartCard>
          </div>

          <ChartCard title="Assigned vs submitted (monthly)">
            <GroupedBarChart
              data={(trends.assignmentTrend || []).map((t) => ({
                name: t.name,
                assigned: t.assigned,
                submitted: t.submitted,
              }))}
              series={[
                { key: "assigned", name: "Assigned", color: C.orange },
                { key: "submitted", name: "Submitted", color: C.emerald },
              ]}
              height={210}
              layout="vertical"
            />
          </ChartCard>

          <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
            <ChartCard title="Exam status">
              <BarChart
                data={distributions.byExamStatus || []}
                layout="horizontal"
                height={200}
                color={C.sky}
                categoryWidth={120}
              />
            </ChartCard>
            <ChartCard title="Avg score % by subject">
              <BarChart
                data={distributions.bySubjectAvg || []}
                layout="horizontal"
                height={200}
                color={C.orange}
                categoryWidth={100}
              />
            </ChartCard>
          </div>

          {(distributions.byExamType || []).length > 0 && (
            <ChartCard title="Exams by type">
              <AreaChart
                data={(distributions.byExamType || []).map((t) => ({
                  period: t.name,
                  value: t.value,
                }))}
                height={160}
                dataKey="value"
                xKey="period"
                color={C.violet}
              />
            </ChartCard>
          )}
        </>
      )}
    </div>
  );
};

export default ExamMarksAnalytics;
